import { useEffect, useRef } from "react"
import { useNavigation, useRoute } from "@react-navigation/native"
import { Platform } from "react-native"
import { useStageFunnel } from "../context/StageFunnelContext"
import { useStageLead } from "./useStageLead"
import {
    ensureUserInUrl,
    isOtherLead,
    readUserParam,
    userNavParams,
} from "@common/utils/funnelLinkIdentity"

// Les écrans du funnel les importent d'ici.
export {
    clearUserFromUrl,
    readUserParam,
    userNavParams,
} from "@common/utils/funnelLinkIdentity"

const IS_WEB = Platform.OS === "web"

/**
 * Lit le stage visé (`?template=<slug>`) sur le lien d'entrée — les liens
 * d'inscription émis par le BO le portent à côté du `user`. Il doit survivre au
 * hop de reprise : un lead à qui l'admin envoie le lien n'a souvent AUCUN état
 * stage, donc `stage/resume` renvoie `state: null` et rebâtit `stage/start` ;
 * sans le slug, l'écran n'a plus de stage à vendre et /start échoue
 * (`stage_unavailable`).
 */
export const readTemplateParam = (route) =>
    route?.params?.template ??
    (IS_WEB && typeof window !== "undefined"
        ? new URLSearchParams(window.location?.search || "").get("template")
        : null)

/**
 * L'intention posée par la landing (`stage_vacances_scolaires_toussaint`), que
 * le fast-track passe à l'entrée du funnel. Lue sur la ROUTE seule : à
 * l'arrivée, elle n'est pas encore dans le contexte.
 */
export const readLeadIntentParam = (route) =>
    route?.params?.leadIntent || route?.params?.lead_intent || null

/**
 * L'étiquette `source` d'un lien d'entrée (`parent_home_banner`…), ramenée à la
 * forme que le serveur garde. Elle suit le parcours dans les événements
 * `stage_funnel_*` et sur l'état créé par /start.
 */
export const cleanSource = (value) => {
    const clean = String(value ?? "")
        .toLowerCase()
        .replace(/[^a-z0-9_]/g, "")
        .slice(0, 40)
    return clean || null
}

export const readSourceParam = (route) =>
    cleanSource(
        route?.params?.source ??
            (IS_WEB && typeof window !== "undefined"
                ? new URLSearchParams(window.location?.search || "").get(
                      "source"
                  )
                : null)
    )

/**
 * Le jeton `user` dérivé de l'email du contexte, quand l'entrée ne le portait
 * pas — un parent venu de la LP HubSpot arrive avec `?email=`, pas `?user=`.
 * Sans lui, l'URL de ses étapes n'était pas copiable : rouverte à froid, elle
 * repartait du début. Même forme que le lien BO (base64 de l'email, ce que
 * `auth_or_email` décode) — c'est une identité, pas un secret.
 */
const userParamFromEmail = (email) => {
    if (!email || typeof btoa !== "function") return null
    try {
        return btoa(email)
    } catch (_e) {
        return null
    }
}

/**
 * Un funnel « froid » = aucune donnée de parcours en mémoire. Survient après un
 * hard-refresh sans sessionStorage ou quand une URL d'étape est ouverte dans un
 * contexte neuf (l'admin copie l'URL, le parent l'ouvre).
 */
const isColdContext = (state) =>
    !state?.funnelStateId &&
    !state?.studentClass &&
    !state?.parentEmail &&
    !state?.paid

/**
 * Le lien vise-t-il un AUTRE stage que celui du parcours en mémoire ? Même
 * famille, onglet resté ouvert (ou restauré par le navigateur) sur son stage
 * précédent : sans cette question, le lien du stage en vente continuait
 * l'ancien parcours — jusqu'à sa confirmation s'il était payé.
 *
 * `resumedTemplate` est le stage pour lequel la reprise a déjà statué : un
 * lien écrit autrement que le slug résolu (`stage_x_2026` pour `x`) ne
 * redemande pas la reprise à chaque écran.
 */
export const isOtherStage = (state, templateParam) =>
    !!templateParam &&
    !!state?.templateSlug &&
    templateParam !== state.resumedTemplate &&
    !namesStage(templateParam, state.templateSlug)

/**
 * Un lien peut nommer le stage par son slug ou par son intention de landing
 * (`stage_<slug>`, suivie ou non d'une saison : `stage_pre_rentree_2026`) — le
 * serveur retire le préfixe puis les segments de fin jusqu'à trouver le stage.
 * Même lecture ici, pour ne pas prendre une autre écriture du même stage pour
 * un autre stage (et renvoyer à la reprise une entrée nominale).
 */
const namesStage = (param, slug) => {
    const bare = (value) => String(value).replace(/^stage_/, "")
    return (
        bare(param) === bare(slug) || bare(param).startsWith(`${bare(slug)}_`)
    )
}

/**
 * Faut-il repasser par la reprise ? Décision pure, testée à part.
 *
 * Passé l'écran de départ, c'est l'IDENTIFIANT du parcours qui décide, pas la
 * présence de réponses : l'état qui casse est l'état partiel, où la classe
 * survit au sessionStorage alors que l'identifiant a disparu. (L'écran de
 * départ ne laisse avancer qu'une fois le parcours créé : une étape sans
 * identifiant l'a donc perdu.) Un parcours payé n'a plus rien à reprendre.
 *
 * L'écran de départ garde la règle « tout est vide » : un parent arrivé par
 * `?email=` y a une identité AVANT d'avoir un parcours, et le renvoyer à la
 * reprise lui dirait que son lien n'est plus valide.
 */
export const needsStageResume = ({ state, funnelStateId, isStart }) => {
    if (state?.paid) return false
    if (isStart) return isColdContext(state) && !funnelStateId
    return !funnelStateId
}

/**
 * Garde unique des écrans du funnel Stage (refresh-safe, partageable par URL),
 * rapprochée de celle du funnel d'inscription. L'URL fait autorité sur ce qui
 * traîne dans l'onglet : sans parcours en mémoire, ou si le lien désigne un
 * autre lead ou un autre stage, elle repasse par `stage/resume` en emportant
 * le stage visé et la source ; sans `user`, retour au début (jamais d'écran
 * blanc). `isStart` exempte l'écran de départ de ce retour au début.
 */
export const useStageFunnelGuard = ({ isStart = false } = {}) => {
    const navigation = useNavigation()
    const route = useRoute()
    const { state, update } = useStageFunnel()
    const { funnelStateId } = useStageLead()
    // Mémorise le jeton déjà arbitré, et non un simple booléen : un changement
    // de lead dans le même onglet doit pouvoir redéclencher la décision.
    const handledRef = useRef(null)

    const urlUserParam = readUserParam(route)
    const userParam = urlUserParam ?? userParamFromEmail(state?.parentEmail)
    const templateParam = readTemplateParam(route)
    const sourceParam = readSourceParam(route)

    useEffect(() => {
        if (userParam) ensureUserInUrl(userParam)
    }, [userParam])

    useEffect(() => {
        if (sourceParam && sourceParam !== state?.entrySource) {
            update({ entrySource: sourceParam })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sourceParam])

    useEffect(() => {
        // Un autre lead, ou un autre stage : l'URL fait autorité sur ce qui
        // traîne dans l'onglet, et la reprise remet à zéro avant de réhydrater.
        const otherLead =
            isOtherLead(state, urlUserParam) ||
            (!!userParam && isOtherStage(state, templateParam))

        // La reprise a DÉJÀ statué sur ce jeton (`resumedToken`, écrit dans le
        // même patch que l'hydratation) : un parcours absent est une réponse
        // du serveur, pas un contexte froid. Sans ce court-circuit, une
        // famille sans parcours rebondirait début → reprise → début.
        if (
            state?.resumedToken &&
            state.resumedToken === userParam &&
            !otherLead
        )
            return

        // Une seule décision de routage par lien (jeton `user` + stage visé)
        // et par montage d'écran : `resumedToken`, lui, vit dans le contexte
        // et tient d'un écran à l'autre.
        const handledKey = `${userParam ?? ""}|${templateParam ?? ""}`
        if (handledRef.current === handledKey) return
        if (!otherLead && !needsStageResume({ state, funnelStateId, isStart }))
            return

        handledRef.current = handledKey

        if (userParam) {
            // Le stage visé et la source accompagnent l'identité : sans le
            // premier, une famille encore sans parcours repart sans savoir
            // quel stage elle achète.
            const params = { user: userParam }
            if (templateParam) params.template = templateParam
            if (sourceParam) params.source = sourceParam
            navigation.reset({
                index: 0,
                routes: [{ name: "stage/resume", params }],
            })
            return
        }

        if (!isStart) {
            navigation.reset({ index: 0, routes: [{ name: "stage/start" }] })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state, userParam, urlUserParam, templateParam, funnelStateId])

    // Le stage visé voyage avec l'identité : un refresh à mi-parcours reprend
    // alors CE stage, pas le dernier parcours de la famille.
    const templateSlug = templateParam ?? state?.templateSlug
    const nextParams =
        userParam && templateSlug
            ? { user: userParam, template: templateSlug }
            : userNavParams(userParam)

    return { userParam, nextParams }
}
